import React, { useMemo, useState } from 'react';
import { Plus, RotateCcw, Share2, Eye, EyeOff, Edit3, Trash2, ChevronDown } from 'lucide-react';
import { IOSButton } from '../ui/custom';
import { useTheme } from '../../contexts/ThemeContext';
import { PageLayout, PageHeader, PageCard, pageStyles } from '../PageLayout';

const TeamView = React.memo(({ users, currentUser, onEditUser, onDeleteUser, onToggleVisibility, onOpenPermissions, onCleanup }: any) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    
    const copy = (text?: string) => {
        if (!text) return;
        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(String(text)).catch(() => {});
        }
    };
    
    const isAdmin = currentUser?.role === 'admin';
    
    const visibleUsers = useMemo(() => {
        if (!users) return [];
        const sorted = [...users].sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
        
        // Nicht-Admins können ausgeblendete Mitarbeiter nicht sehen
        if (!isAdmin) {
            return sorted.filter((u: any) => !u.hidden);
        }
        
        return sorted;
    }, [users, isAdmin]);

    return (
        <PageLayout>
            <PageHeader
                title="Team"
                subtitle={`${visibleUsers.length} Mitarbeiter`}
                actions={isAdmin && (
                    <IOSButton icon={Plus} onClick={() => onEditUser({})}>Mitarbeiter anlegen</IOSButton>
                )}
            />

            {visibleUsers.length === 0 ? (
                <PageCard>
                    <div className={pageStyles.empty(isDark)}>
                        <p className="font-medium">Keine Mitarbeiter gefunden</p>
                    </div>
                </PageCard>
            ) : (
                <div className="grid grid-cols-1 gap-3">
                    {visibleUsers.map((u) => (
                        <div
                            key={u.id}
                            className={`${isDark ? 'bg-slate-800/80 border-slate-600/50 hover:border-slate-500/50' : 'bg-white border-slate-200 hover:border-slate-300'} border rounded-2xl p-4 transition-all hover:shadow-md ${u.hidden ? 'opacity-75' : ''}`}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center font-bold text-white text-lg flex-shrink-0">
                                        {u.name?.[0]?.toUpperCase() || '?'}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap mb-2">
                                            <p className={`font-bold text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>{u.name}</p>
                                            <div className="flex gap-2 flex-wrap">
                                                {u.role === 'admin' && <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${isDark ? 'bg-purple-500/30 text-purple-200' : 'bg-purple-100 text-purple-700'} uppercase`}>Admin</span>}
                                                {u.level && <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${isDark ? 'bg-blue-500/30 text-blue-200' : 'bg-blue-100 text-blue-700'} uppercase`}>{u.level}</span>}
                                                {u.hidden && <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${isDark ? 'bg-yellow-500/30 text-yellow-200' : 'bg-yellow-100 text-yellow-700'} uppercase`}>Inaktiv</span>}
                                            </div>
                                        </div>
                                        <div className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-700'} flex flex-wrap gap-2 items-center`}>
                                            {u.uNumber && (
                                                <span className={`px-2 py-1 rounded-md border text-[11px] flex items-center gap-1 ${isDark ? 'border-white/10 bg-white/5 text-white/80' : 'border-slate-200 bg-slate-100 text-slate-700'}`}>
                                                    <strong className="font-semibold">U-Nr:</strong>
                                                    <span className="font-mono">{u.uNumber}</span>
                                                    <button onClick={(e) => { e.stopPropagation(); copy(u.uNumber); }} title="Kopieren" className={`${isDark ? 'hover:text-cyan-300 text-white/50' : 'hover:text-cyan-600 text-slate-500'}`}>
                                                        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                                                            <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v-1a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v1zm5 0v-1a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1v1h4z"/>
                                                        </svg>
                                                    </button>
                                                </span>
                                            )}
                                            {u.avusPassword && (
                                                <span className={`px-2 py-1 rounded-md border text-[11px] flex items-center gap-1 ${isDark ? 'border-white/10 bg-white/5 text-white/80' : 'border-slate-200 bg-slate-100 text-slate-700'}`}>
                                                    <strong className="font-semibold">AVUS:</strong>
                                                    <span className="font-mono">{u.avusPassword}</span>
                                                    <button onClick={(e) => { e.stopPropagation(); copy(u.avusPassword); }} title="Kopieren" className={`${isDark ? 'hover:text-cyan-300 text-white/50' : 'hover:text-cyan-600 text-slate-500'}`}>
                                                        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                                                            <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v-1a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v1zm5 0v-1a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1v1h4z"/>
                                                        </svg>
                                                    </button>
                                                </span>
                                            )}
                                            {(u.adv || u.btnr) && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setExpandedId(expandedId === u.id ? null : u.id); }}
                                                    aria-expanded={expandedId === u.id}
                                                    aria-controls={`user-details-${u.id}`}
                                                    className={`flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] font-semibold transition-colors ${isDark ? 'border-white/10 bg-white/5 text-white/80 hover:bg-white/10' : 'border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                                                >
                                                    <ChevronDown size={14} className={`transition-transform ${expandedId === u.id ? 'rotate-180' : ''}`} />
                                                    Weitere Angaben
                                                </button>
                                            )}
                                        </div>
                                        {expandedId === u.id && (
                                            <div id={`user-details-${u.id}`} className={`mt-2 p-2 rounded-lg border text-xs flex flex-wrap gap-2 ${isDark ? 'border-white/10 bg-white/5 text-white/70' : 'border-slate-200 bg-slate-50 text-slate-700'}`}>
                                                {u.adv && (
                                                    <span className="px-2 py-1 rounded-md border border-transparent font-mono">ADV: {u.adv}</span>
                                                )}
                                                {u.btnr && (
                                                    <span className="px-2 py-1 rounded-md border border-transparent font-mono">BTNR: {u.btnr}</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Aktions-Buttons */}
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    {(isAdmin || u.id === currentUser?.id) && (
                                        <button onClick={() => onEditUser(u)} title="Bearbeiten" className={`p-2 rounded-lg transition-colors ${isDark ? 'text-cyan-400/70 hover:text-cyan-300 hover:bg-cyan-500/20' : 'text-cyan-600/70 hover:text-cyan-700 hover:bg-cyan-100'}`}>
                                            <Edit3 size={18} />
                                        </button>
                                    )}
                                    {isAdmin && (
                                        <>
                                            <button onClick={() => onOpenPermissions(u)} title="Rechte" className={`p-2 rounded-lg transition-colors ${isDark ? 'text-indigo-400/70 hover:text-indigo-300 hover:bg-indigo-500/20' : 'text-indigo-600/70 hover:text-indigo-700 hover:bg-indigo-100'}`}>
                                                <Share2 size={18} />
                                            </button>
                                            <button onClick={() => onToggleVisibility(u)} title={u.hidden ? 'Aktivieren' : 'Deaktivieren'} className={`p-2 rounded-lg transition-colors ${isDark ? 'text-white/50 hover:text-white/80 hover:bg-white/10' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200'}`}>
                                                {u.hidden ? <EyeOff size={18}/> : <Eye size={18}/>}
                                            </button>
                                            <button onClick={() => onDeleteUser(u.id)} title="Löschen" className={`p-2 rounded-lg transition-colors ${isDark ? 'text-red-400/70 hover:text-red-300 hover:bg-red-500/20' : 'text-red-600/70 hover:text-red-700 hover:bg-red-100'}`}>
                                                <Trash2 size={18} />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </PageLayout>
    );
});

TeamView.displayName = 'TeamView';
export default TeamView;
